import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TruckAnnouncementService } from '../../core/api/truck-announcement.service';
import { ReferenceDataService } from '../../core/api/reference-data.service';
import { ServiceRequestService } from '../../core/api/service-request.service';
import { TruckAnnouncement } from '../../shared/models/truck-announcement';
import { LookupOption } from '../../shared/models/lookup-option';
import { ServiceRequestListItem } from '../../shared/models/service-request';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { I18nService } from '../../shared/i18n.service';
import { parseApiValidationErrors } from '../../shared/validation-error.util';

const LUKA_BAR_NAME = 'LUKA BAR AD';
const NIMONT_NAME = 'NIMONT';
const SENT_STATUS = 'sent';

@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ (isEdit() ? 'ta.editTitle' : 'ta.newTitle') | t }}</h1>
      </header>

      @if (loading()) { <div class="skeleton-detail">
        <div class="skeleton-heading"></div>
        <div class="skeleton-line w-70"></div>
        <div class="skeleton-line w-55"></div>
        <div class="skeleton-line w-85"></div>
        <div class="skeleton-line w-40"></div>
      </div> }
      @if (errorList().length) {
        <div class="error notice validation-errors">
          <strong>{{ 'validation.title' | t }}</strong>
          <ul>
            @for (msg of errorList(); track msg) { <li>{{ msg }}</li> }
          </ul>
        </div>
      }

      @if (!loading()) {
        <form class="form-card" [formGroup]="form" (ngSubmit)="save()">
          <div class="form-grid">
            <label>{{ 'ta.announcementFrom' | t }} <span class="required">*</span>
              <input type="date" formControlName="announcementFrom" (change)="onFromDateChange()" />
              @if (form.controls.announcementFrom.touched && form.controls.announcementFrom.invalid) {
                <span class="field-error">{{ 'ta.fieldRequired' | t }}</span>
              }
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label>{{ 'ta.announcementTo' | t }} <span class="required">*</span>
              <input type="date" formControlName="announcementTo" />
              @if (form.controls.announcementTo.touched && form.controls.announcementTo.invalid) {
                <span class="field-error">{{ 'ta.fieldRequired' | t }}</span>
              }
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label>{{ 'ta.terminalOperator' | t }} <span class="required">*</span>
              <select formControlName="terminalOperatorCode" (change)="onTerminalOperatorChange()">
                <option value="">{{ 'ta.selectTerminalOperator' | t }}</option>
                @for (x of terminalOperators(); track x.Id ?? x.Code ?? $index) { <option [value]="x.Code">{{ x.Name || x.Code }}</option> }
              </select>
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label>{{ 'ta.serviceRequest' | t }}
              @if (isLukaBar()) {
                <select formControlName="serviceRequestPCSId">
                  <option value="">{{ 'ta.selectServiceRequest' | t }}</option>
                  @for (x of serviceRequestOptions(); track x.Id) {
                    <option [value]="x.Id">#{{ x.ServiceRequestNumber ?? x.Id }} — {{ x.CustomerName || x.ServiceRequestTypeName || x.Id }}</option>
                  }
                </select>
                @if (loadingServiceRequests()) { <span class="sr-loading">{{ 'ta.serviceRequestSearching' | t }}</span> }
              } @else {
                <input formControlName="serviceRequest" />
              }
            </label>

            <label>{{ 'common.warehouse' | t }}
              <select formControlName="warehouseCode">
                <option value="">{{ 'ta.selectWarehouse' | t }}</option>
                @for (x of warehouses(); track x.Id ?? x.Code ?? $index) { <option [value]="x.Code">{{ x.Name || x.Code }}</option> }
              </select>
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label>{{ 'ta.cargoDescription' | t }}
              <select formControlName="parkingCargoDescriptionId">
                <option value="">{{ 'ta.selectCargoDescription' | t }}</option>
                @for (x of cargoDescriptions(); track x.Id ?? x.Code ?? $index) { <option [value]="x.Id">{{ x.Name || x.Code }}</option> }
              </select>
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label>{{ 'ta.trucksQuantity' | t }}
              <input type="number" min="0" formControlName="trucksQuantity" />
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label class="checkbox-label">
              <input type="checkbox" formControlName="directPortEntrance" (change)="onDirectPortEntranceChange()" />
              {{ 'ta.directPortEntrance' | t }}
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label>{{ 'ta.licencePlate' | t }}
              @if (directPortEntranceValue()) {
                <select formControlName="frontVehicleCodeId">
                  <option value="">{{ 'ta.selectVehicle' | t }}</option>
                  @for (v of vehicleOptions(); track v.Id) { <option [value]="v.Id">{{ v.Name || v.Code }}</option> }
                </select>
                @if (loadingVehicles()) { <span class="field-hint">{{ 'ta.serviceRequestSearching' | t }}</span> }
              } @else {
                <input formControlName="licencePlate" />
              }
              @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>

            <label>{{ 'ta.permitUserBadgeNumber' | t }}
              <select formControlName="permitUserBadgeId">
                <option value="">{{ 'ta.selectBadge' | t }}</option>
                @for (b of permitBadges(); track b.Id) { <option [value]="b.Id">{{ b['Badge'] ?? b.Code }} — {{ b.Name }}</option> }
              </select>
              @if (isSent() && directPortEntranceValue()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
            </label>
          </div>

          <label class="wide-label">{{ 'ta.note' | t }}
            <textarea rows="4" formControlName="note"></textarea>
            @if (isSent()) { <span class="field-hint">{{ 'ta.lockedWhenSent' | t }}</span> }
          </label>

          @if (form.controls.terminalOperatorCode.touched && form.controls.terminalOperatorCode.invalid) {
            <p class="field-error">{{ 'ta.terminalOperatorRequired' | t }}</p>
          }

          <div class="form-actions">
            <a class="button secondary" [routerLink]="backLink()">{{ 'common.cancel' | t }}</a>
            <button class="button" type="submit" [disabled]="saving()">{{ (saving() ? 'common.saving' : 'common.save') | t }}</button>
          </div>
        </form>
      }
    </main>
  `
})
export class TruckAnnouncementFormPage implements OnInit {
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorList = signal<string[]>([]);
  readonly warehouses = signal<LookupOption[]>([]);
  readonly terminalOperators = signal<LookupOption[]>([]);
  readonly cargoDescriptions = signal<LookupOption[]>([]);
  private id: string | null = null;
  private readonly fb = inject(FormBuilder);

  readonly isEdit = computed(() => !!this.id);
  readonly backLink = computed(() => this.id ? `/truck-announcements/${this.id}` : '/truck-announcements');

  readonly isLukaBar = signal(false);
  readonly isSent = signal(false);
  readonly serviceRequestOptions = signal<ServiceRequestListItem[]>([]);
  readonly loadingServiceRequests = signal(false);
  readonly directPortEntranceValue = signal(false);
  readonly vehicleOptions = signal<LookupOption[]>([]);
  readonly loadingVehicles = signal(false);
  readonly permitBadges = signal<LookupOption[]>([]);
  private savedFrontVehicleCodeId: number | null = null;

  readonly form = this.fb.group({
    terminalOperatorCode: ['', Validators.required],
    serviceRequest: [''],
    serviceRequestPCSId: ['' as string | number],
    warehouseCode: [''],
    parkingCargoDescriptionId: ['' as string | number],
    trucksQuantity: [null as number | null],
    announcementFrom: ['', Validators.required],
    announcementTo: ['', Validators.required],
    directPortEntrance: [false],
    licencePlate: [''],
    frontVehicleCodeId: ['' as string | number],
    permitUserBadgeId: ['' as string | number],
    note: ['']
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly api: TruckAnnouncementService,
    private readonly refs: ReferenceDataService,
    private readonly serviceRequestApi: ServiceRequestService,
    private readonly i18n: I18nService
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    const lookups$ = forkJoin({
      warehouses: this.refs.warehouses(),
      terminalOperators: this.refs.terminalOperators(),
      cargoDescriptions: this.refs.parkingCargoDescriptions()
    });

    this.refs.permitUserBadges().subscribe({ next: badges => this.permitBadges.set(badges), error: () => {} });

    if (this.id) {
      forkJoin({ lookups: lookups$, item: this.api.get(this.id) }).subscribe({
        next: ({ lookups, item }) => {
          this.applyLookups(lookups);
          this.patch(item);
          this.loading.set(false);
          this.syncLukaBarState();
          this.applySentLock(item);
          this.savedFrontVehicleCodeId = item.FrontVehicleCodeId ?? null;
          this.directPortEntranceValue.set(item.DirectPortEntrance ?? false);
          if (item.DirectPortEntrance) this.loadVehicleOptions();
        },
        error: err => { this.setError(err, 'ta.formLoadError'); this.loading.set(false); }
      });
    } else {
      lookups$.subscribe({
        next: lookups => { this.applyLookups(lookups); this.loading.set(false); },
        error: err => { this.setError(err, 'ta.refLoadError'); this.loading.set(false); }
      });
    }
  }

  onTerminalOperatorChange(): void {
    this.syncLukaBarState(true);
    const code = this.form.controls.terminalOperatorCode.value;
    const op = this.terminalOperators().find(x => x.Code === code);
    // Per spec: DirectPortEntrance is fully driven by the terminal operator —
    // Nimont forces it on, every other operator forces it off.
    const isNimont = (op?.Name ?? '').trim().toUpperCase() === NIMONT_NAME;
    this.form.patchValue({ directPortEntrance: isNimont });
    this.directPortEntranceValue.set(isNimont);
    if (isNimont) {
      this.form.patchValue({ licencePlate: '' });
      this.vehicleOptions.set([]);
      this.loadVehicleOptions();
    } else {
      this.form.patchValue({ frontVehicleCodeId: '' });
    }
  }

  onDirectPortEntranceChange(): void {
    const checked = this.form.controls.directPortEntrance.value ?? false;
    this.directPortEntranceValue.set(checked);
    if (checked) {
      this.form.patchValue({ licencePlate: '' });
      this.loadVehicleOptions();
    } else {
      this.form.patchValue({ frontVehicleCodeId: '' });
    }
  }

  private loadVehicleOptions(): void {
    this.loadingVehicles.set(true);
    const operatorCode = this.form.controls.terminalOperatorCode.value || undefined;
    this.refs.truckModuleVehicles(operatorCode).subscribe({
      next: vehicles => {
        this.vehicleOptions.set(vehicles);
        this.loadingVehicles.set(false);
        if (this.savedFrontVehicleCodeId != null) {
          setTimeout(() => this.form.patchValue({ frontVehicleCodeId: this.savedFrontVehicleCodeId }));
        }
      },
      error: () => { this.loadingVehicles.set(false); }
    });
  }

  onFromDateChange(): void {
    const from = this.form.controls.announcementFrom.value;
    if (!from) return;
    const date = new Date(from + 'T00:00:00');
    if (Number.isNaN(date.getTime())) return;
    date.setDate(date.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    const to = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    this.form.patchValue({ announcementTo: to });
  }

  private syncLukaBarState(clearOnSwitch = false): void {
    const code = this.form.controls.terminalOperatorCode.value;
    const op = this.terminalOperators().find(x => x.Code === code);
    const wasLukaBar = this.isLukaBar();
    const nowLukaBar = ((op?.Name ?? '').trim().toUpperCase() === LUKA_BAR_NAME);
    this.isLukaBar.set(nowLukaBar);
    if (nowLukaBar) this.loadServiceRequestOptions();
    if (!clearOnSwitch) return;
    if (nowLukaBar && !wasLukaBar) {
      this.form.patchValue({ serviceRequest: '' });
    } else if (!nowLukaBar && wasLukaBar) {
      this.form.patchValue({ serviceRequestPCSId: '' });
    }
  }

  private applySentLock(item: TruckAnnouncement): void {
    const sent = (item.TruckAnnouncementStatusName ?? '').trim().toLowerCase() === SENT_STATUS;
    this.isSent.set(sent);
    if (!sent) return;
    this.form.controls.trucksQuantity.disable();
    this.form.controls.directPortEntrance.disable();
    this.form.controls.note.disable();
    this.form.controls.terminalOperatorCode.disable();
    this.form.controls.warehouseCode.disable();
    this.form.controls.parkingCargoDescriptionId.disable();
    this.form.controls.announcementFrom.disable();
    this.form.controls.announcementTo.disable();
    this.form.controls.licencePlate.disable();
    this.form.controls.frontVehicleCodeId.disable();
    // Badge only locks when Direct Port Entrance is active, per spec.
    if (item.DirectPortEntrance) this.form.controls.permitUserBadgeId.disable();
  }

  private loadServiceRequestOptions(): void {
    if (this.serviceRequestOptions().length || this.loadingServiceRequests()) return;
    this.loadingServiceRequests.set(true);
    this.serviceRequestApi.search({ Page: 1, PageSize: 50 }).subscribe({
      next: items => { this.serviceRequestOptions.set(items); this.loadingServiceRequests.set(false); },
      error: () => { this.loadingServiceRequests.set(false); }
    });
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.saving.set(true);
    this.errorList.set([]);

    const raw = this.form.getRawValue();
    const pickedSr = this.isLukaBar() && raw.serviceRequestPCSId
      ? this.serviceRequestOptions().find(x => x.Id === Number(raw.serviceRequestPCSId))
      : undefined;
    const pickedVehicle = raw.directPortEntrance && raw.frontVehicleCodeId
      ? this.vehicleOptions().find(v => v.Id === Number(raw.frontVehicleCodeId))
      : undefined;
    const model: TruckAnnouncement = {
      ...(this.id ? { Id: Number(this.id) } : {}),
      TerminalOperatorCode: raw.terminalOperatorCode || undefined,
      ServiceRequestPCSId: this.isLukaBar() ? (Number(raw.serviceRequestPCSId) || undefined) : undefined,
      ServiceRequest: this.isLukaBar()
        ? (pickedSr ? String(pickedSr.ServiceRequestNumber ?? pickedSr.Id) : undefined)
        : (raw.serviceRequest || undefined),
      WarehouseCode: raw.warehouseCode || undefined,
      ParkingCargoDescriptionId: raw.parkingCargoDescriptionId || undefined,
      TrucksQuantity: raw.trucksQuantity ?? undefined,
      AnnouncementFrom: this.api.toApiDate(raw.announcementFrom ? `${raw.announcementFrom}T02:00` : undefined),
      AnnouncementTo: this.api.toApiDate(raw.announcementTo ? `${raw.announcementTo}T02:00` : undefined),
      DirectPortEntrance: raw.directPortEntrance ?? false,
      // Not yet persisted by the API (TruckAnnouncement DTO doesn't expose these) — sent so
      // this starts working automatically once the backend adds support. See API-FIXES-NEEDED.md.
      LicencePlate: raw.directPortEntrance ? undefined : (raw.licencePlate || undefined),
      FrontVehicleCodeId: raw.directPortEntrance ? (Number(raw.frontVehicleCodeId) || undefined) : undefined,
      FrontVehicleCode: pickedVehicle ? (pickedVehicle.Name || String(pickedVehicle.Code)) : undefined,
      PermitUserBadgeId: Number(raw.permitUserBadgeId) || undefined,
      Note: raw.note || undefined
    };

    const request$ = this.id ? this.api.edit(model) : this.api.add(model);
    request$.subscribe({
      next: response => {
        this.saving.set(false);
        const id = this.api.extractId(response) ?? this.id;
        void this.router.navigate(id ? ['/truck-announcements', id] : ['/truck-announcements']);
      },
      error: err => { this.setError(err, 'ta.saveError'); this.saving.set(false); }
    });
  }

  private patch(item: TruckAnnouncement): void {
    this.form.patchValue({
      terminalOperatorCode: item.TerminalOperatorCode ?? '',
      serviceRequest: item.ServiceRequest ?? '',
      serviceRequestPCSId: item.ServiceRequestPCSId ?? '',
      warehouseCode: item.WarehouseCode ?? '',
      parkingCargoDescriptionId: item.ParkingCargoDescriptionId ?? '',
      trucksQuantity: item.TrucksQuantity ?? null,
      announcementFrom: this.api.toLocalInputDate(item.AnnouncementFrom).slice(0, 10),
      announcementTo: this.api.toLocalInputDate(item.AnnouncementTo).slice(0, 10),
      directPortEntrance: item.DirectPortEntrance ?? false,
      licencePlate: item.LicencePlate ?? '',
      frontVehicleCodeId: item.FrontVehicleCodeId ?? '',
      permitUserBadgeId: item.PermitUserBadgeId ?? '',
      note: item.Note ?? ''
    });
  }

  private applyLookups(x: { warehouses: LookupOption[]; terminalOperators: LookupOption[]; cargoDescriptions: LookupOption[] }): void {
    this.warehouses.set(x.warehouses);
    this.terminalOperators.set(x.terminalOperators);
    this.cargoDescriptions.set(x.cargoDescriptions);
  }

  private setError(err: any, fallbackKey: string): void {
    const parsed = parseApiValidationErrors(err, this.i18n);
    this.errorList.set(parsed.length ? parsed : [this.i18n.translate(fallbackKey)]);
  }
}
